import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import FileSpreadsheet from 'lucide-react/dist/esm/icons/file-spreadsheet';
import FilterIcon from 'lucide-react/dist/esm/icons/filter';
import X from 'lucide-react/dist/esm/icons/x';
import { PageSizeSelect, SortableHeader, TableEmptyRow, CURRENCY_FMT, formatMoney, parseMoney, parseDateLocal, formatDate, downloadBlob } from '../shared';
import { useAmlStore } from '../../stores/amlStore';
import { amlApi } from '../../services/amlApi';
import { DatePicker, SearchInput, Tooltip, Checkbox, SlidePanel } from '../../../../components/ui';
import { Button, Spinner, Text, Pagination } from '../../../../components/ui';
import type { AmlTransaction } from '../../types/aml';
import { useTranslation } from '../../../../hooks';
import { useUIStore } from '../../../../stores/uiStore';

const formatDateStr = formatDate;

const toCsvRow = (row: string[]): string =>
  row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');

const ALL_COLUMN_KEYS = [
  'transaction_date', 'customer_name', 'location_name', 'inbound', 'outbound',
  'amount_collected', 'fee_inbound', 'fee_outbound', 'fee_collected',
  'invoice_number', 'invoice_date', 'invoice_amount', 'stops', 'pcs', 'notes',
] as const;

const defaultVisible: Record<string, boolean> = Object.fromEntries(ALL_COLUMN_KEYS.map((k) => [k, true]));

const MONEY_COLS = new Set(['inbound', 'outbound', 'amount_collected', 'fee_inbound', 'fee_outbound', 'fee_collected', 'invoice_amount']);
const RIGHT_COLS = new Set([...Array.from(MONEY_COLS), 'stops', 'pcs']);
const DATE_COLS  = new Set(['transaction_date', 'invoice_date']);
const TRUNC_COLS = new Set(['customer_name', 'location_name', 'notes']);

const TransactionReportView: React.FC = () => {
  const {
    customersForFilter,
    locations,
    fetchCustomersForFilter,
    fetchLocations,
  } = useAmlStore(
    useShallow((s) => ({
      customersForFilter: s.customersForFilter,
      locations: s.locations,
      fetchCustomersForFilter: s.fetchCustomersForFilter,
      fetchLocations: s.fetchLocations,
    })),
  );

  const { language } = useUIStore();
  const { t: tr } = useTranslation(language);

  const ALL_COLUMNS = useMemo(() => [
    { key: 'transaction_date',  label: tr('amlColDate') },
    { key: 'customer_name',     label: tr('amlColCustomerLegalName') },
    { key: 'location_name',     label: tr('amlColLocation') },
    { key: 'inbound',           label: tr('amlColInbound') },
    { key: 'outbound',          label: tr('amlColOutbound') },
    { key: 'amount_collected',  label: tr('amlColAmount') },
    { key: 'fee_inbound',       label: tr('amlColFeeInbound') },
    { key: 'fee_outbound',      label: tr('amlColFeeOutbound') },
    { key: 'fee_collected',     label: tr('amlColAmountFee') },
    { key: 'invoice_number',    label: tr('amlColInvoiceNumber') },
    { key: 'invoice_date',      label: tr('amlColInvoiceDate') },
    { key: 'invoice_amount',    label: tr('amlColInvoiceAmount') },
    { key: 'stops',             label: tr('amlColStops') },
    { key: 'pcs',               label: tr('amlColPcs') },
    { key: 'notes',             label: tr('amlColNotes') },
  ], [tr]);

  const [reportData, setReportData]   = useState<AmlTransaction[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');

  const [filterOpen, setFilterOpen]       = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');
  const [invDateFrom, setInvDateFrom]     = useState('');
  const [invDateTo, setInvDateTo]         = useState('');
  const [visibleCols, setVisibleCols]     = useState<Record<string, boolean>>(defaultVisible);

  const [appliedCustomerIds, setAppliedCustomerIds]   = useState<string[]>([]);
  const [appliedLocationIds, setAppliedLocationIds]   = useState<string[]>([]);
  const [appliedDateFrom, setAppliedDateFrom]         = useState('');
  const [appliedDateTo, setAppliedDateTo]             = useState('');
  const [appliedInvDateFrom, setAppliedInvDateFrom]   = useState('');
  const [appliedInvDateTo, setAppliedInvDateTo]       = useState('');

  const [downloading, setDownloading] = useState<'pdf' | 'csv' | null>(null);
  const [sortField, setSortField]     = useState<string>('transaction_date');
  const [sortDir, setSortDir]         = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize]       = useState(20);
  const [page, setPage]               = useState(1);
  const mountedRef = useRef(true);
  const reportSlotRef = useRef<{ key: string; promise: Promise<AmlTransaction[]> } | null>(null);

  const fetchReport = useCallback(async (
    cids: string[], lids: string[], df: string, dt: string, idf: string, idt: string
  ) => {
    const params = {
      customer_id:    cids.length ? cids : undefined,
      location_id:    lids.length ? lids : undefined,
      date_from:      df  || undefined,
      date_to:        dt  || undefined,
      inv_date_from:  idf || undefined,
      inv_date_to:    idt || undefined,
    };
    const key = `${cids.join(',')}:${lids.join(',')}:${df}:${dt}:${idf}:${idt}`;
    setLoading(true);
    try {
      if (reportSlotRef.current?.key === key) {
        const data = await reportSlotRef.current.promise;
        if (mountedRef.current) setReportData(data);
      } else {
        const promise = amlApi.getTransactionReport(params);
        reportSlotRef.current = { key, promise };
        const data = await promise;
        if (mountedRef.current) setReportData(data);
      }
    } catch (e) {
      console.error('Failed to fetch report', e);
      if (mountedRef.current) setReportData([]);
    } finally {
      if (reportSlotRef.current?.key === key) reportSlotRef.current = null;
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchCustomersForFilter();
    fetchLocations();
    return () => { mountedRef.current = false; };
  }, [fetchCustomersForFilter, fetchLocations]);

  useEffect(() => { setPage(1); }, [pageSize]);

  useEffect(() => {
    if (selectedCustomerIds.length === 1) fetchLocations(selectedCustomerIds[0]);
    else fetchLocations();
    setSelectedLocationIds([]);
  }, [selectedCustomerIds, fetchLocations]);

  const handleApplyFilter = useCallback(() => {
    setAppliedCustomerIds(selectedCustomerIds);
    setAppliedLocationIds(selectedLocationIds);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setAppliedInvDateFrom(invDateFrom);
    setAppliedInvDateTo(invDateTo);
    setFilterOpen(false);
    setPage(1);
    setHasSearched(true);
    fetchReport(selectedCustomerIds, selectedLocationIds, dateFrom, dateTo, invDateFrom, invDateTo);
  }, [selectedCustomerIds, selectedLocationIds, dateFrom, dateTo, invDateFrom, invDateTo, fetchReport]);

  const handleResetFilter = useCallback(() => {
    setSelectedCustomerIds([]);
    setSelectedLocationIds([]);
    setCustomerSearch('');
    setLocationSearch('');
    setDateFrom('');
    setDateTo('');
    setInvDateFrom('');
    setInvDateTo('');
    setVisibleCols(defaultVisible);
    setAppliedCustomerIds([]);
    setAppliedLocationIds([]);
    setAppliedDateFrom('');
    setAppliedDateTo('');
    setAppliedInvDateFrom('');
    setAppliedInvDateTo('');
    setPage(1);
    setHasSearched(false);
    setReportData([]);
  }, []);

  const hasPendingFilters = !!(
    selectedCustomerIds.length > 0 ||
    selectedLocationIds.length > 0 ||
    dateFrom || dateTo || invDateFrom || invDateTo
  );

  const hasAppliedFilters = !!(
    appliedCustomerIds.length > 0 ||
    appliedLocationIds.length > 0 ||
    appliedDateFrom || appliedDateTo || appliedInvDateFrom || appliedInvDateTo
  );

  const activeFilterCount = useMemo(() => [
    appliedCustomerIds.length > 0,
    appliedLocationIds.length > 0,
    !!appliedDateFrom, !!appliedDateTo,
    !!appliedInvDateFrom, !!appliedInvDateTo,
  ].filter(Boolean).length, [appliedCustomerIds, appliedLocationIds, appliedDateFrom, appliedDateTo, appliedInvDateFrom, appliedInvDateTo]);

  const handleDownloadPdf = useCallback(async () => {
    setDownloading('pdf');
    try {
      const blob = await amlApi.downloadTransactionReport({
        customer_ids:   appliedCustomerIds.length  ? appliedCustomerIds  : undefined,
        location_ids:   appliedLocationIds.length  ? appliedLocationIds  : undefined,
        date_from:      appliedDateFrom   || undefined,
        date_to:        appliedDateTo     || undefined,
        inv_date_from:  appliedInvDateFrom || undefined,
        inv_date_to:    appliedInvDateTo   || undefined,
      });
      downloadBlob(blob as Blob, 'transaction-report.pdf');
    } catch (e) {
      console.error('Failed to download PDF', e);
    } finally {
      setDownloading(null);
    }
  }, [appliedCustomerIds, appliedLocationIds, appliedDateFrom, appliedDateTo, appliedInvDateFrom, appliedInvDateTo]);

  const handleSort = useCallback((field: string, dir: 'asc' | 'desc') => {
    setSortField(field);
    setSortDir(dir);
    setPage(1);
  }, []);

  const sorted = useMemo(() => {
    let result = [...reportData];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((t) =>
        (t.customer_name && t.customer_name.toLowerCase().includes(q)) ||
        (t.location_name && t.location_name.toLowerCase().includes(q)) ||
        (t.invoice_number && t.invoice_number.toLowerCase().includes(q))
      );
    }
    result.sort((a, b) => {
      let va: any = (a as any)[sortField] ?? '';
      let vb: any = (b as any)[sortField] ?? '';
      if (DATE_COLS.has(sortField)) {
        va = va ? parseDateLocal(va).getTime() : 0;
        vb = vb ? parseDateLocal(vb).getTime() : 0;
      } else if (typeof va === 'string') {
        const n = parseFloat(va);
        if (!isNaN(n)) va = n; else va = va.toLowerCase();
        const m = parseFloat(vb as string);
        if (!isNaN(m)) vb = m; else vb = (vb as string).toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [reportData, sortField, sortDir, search]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const totals = useMemo(() => sorted.reduce(
    (acc, t) => ({
      inbound:     acc.inbound     + parseMoney(t.inbound),
      outbound:    acc.outbound    + parseMoney(t.outbound),
      collected:   acc.collected   + parseMoney(t.amount_collected),
      feeInbound:  acc.feeInbound  + parseMoney(t.fee_inbound),
      feeOutbound: acc.feeOutbound + parseMoney(t.fee_outbound),
      feeCollected:acc.feeCollected+ parseMoney(t.fee_collected),
      invoice:     acc.invoice     + parseMoney(t.invoice_amount),
      stops:       acc.stops       + (t.stops ?? 0),
      pcs:         acc.pcs         + (t.pcs ?? 0),
    }),
    { inbound: 0, outbound: 0, collected: 0, feeInbound: 0, feeOutbound: 0, feeCollected: 0, invoice: 0, stops: 0, pcs: 0 }
  ), [sorted]);

  const filteredCustomers = useMemo(() =>
    customersForFilter.filter((c) =>
      !customerSearch || c.name.toLowerCase().includes(customerSearch.toLowerCase())
    ), [customersForFilter, customerSearch]);

  const filteredLocations = useMemo(() => {
    const seen = new Set<string>();
    return locations.filter((l) => {
      if (seen.has(l.id)) return false;
      seen.add(l.id);
      return !locationSearch || l.name.toLowerCase().includes(locationSearch.toLowerCase());
    });
  }, [locations, locationSearch]);

  const visibleColList = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleCols[c.key]),
    [ALL_COLUMNS, visibleCols],
  );

  const colWidthMap: Record<string, string> = useMemo(() => ({
    transaction_date:  '100px',
    customer_name:     '180px',
    location_name:     '160px',
    inbound:           '110px',
    outbound:          '110px',
    amount_collected:  '150px',
    fee_inbound:       '110px',
    fee_outbound:      '110px',
    fee_collected:     '160px',
    invoice_number:    '120px',
    invoice_date:      '110px',
    invoice_amount:    '120px',
    stops:             '70px',
    pcs:               '70px',
    notes:             '140px',
  }), []);

  const totalForCol = useMemo<Record<string, string | number | null>>(() => ({
    transaction_date:  null,
    customer_name:     null,
    location_name:     null,
    inbound:           CURRENCY_FMT.format(totals.inbound),
    outbound:          CURRENCY_FMT.format(totals.outbound),
    amount_collected:  CURRENCY_FMT.format(totals.collected),
    fee_inbound:       CURRENCY_FMT.format(totals.feeInbound),
    fee_outbound:      CURRENCY_FMT.format(totals.feeOutbound),
    fee_collected:     CURRENCY_FMT.format(totals.feeCollected),
    invoice_number:    null,
    invoice_date:      null,
    invoice_amount:    CURRENCY_FMT.format(totals.invoice),
    stops:             totals.stops,
    pcs:               totals.pcs,
    notes:             null,
  }), [totals]);

  const handleExportCsv = useCallback(() => {
    setDownloading('csv');
    const headers = visibleColList.map((c) => c.label);
    const rows = sorted.map((t) =>
      toCsvRow(
        visibleColList.map(({ key }) => {
          const v = (t as any)[key];
          if (v == null) return '';
          if (DATE_COLS.has(key)) return v ? formatDateStr(v) : '';
          return String(v);
        })
      )
    );
    const csv = [headers.join(','), ...rows].join('\n');
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), 'transaction-report.csv');
    setDownloading(null);
  }, [visibleColList, sorted]);

  const openFilterPanel = useCallback(() => setFilterOpen(true), []);
  const closeFilterPanel = useCallback(() => setFilterOpen(false), []);

  const handlePageSizeChange = useCallback((s: number) => {
    setPageSize(s);
    setPage(1);
  }, []);

  return (
    <div className="space-y-4">
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 overflow-visible pt-2">
        <div className="flex items-center gap-3 overflow-visible py-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={tr('amlPlaceholderSearchByCustomerLocation')}
            className="w-56"
            size="sm"
          />

          <div className="relative">
            <button
              onClick={openFilterPanel}
              className="p-2.5 rounded-xl border border-subtle bg-surface-primary text-content-secondary hover:bg-surface-secondary transition-colors"
              aria-label={tr('amlFilterTitle')}
            >
              <FilterIcon size={16} />
            </button>
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 bg-brand text-[10px] text-white rounded-full flex items-center justify-center font-bold pointer-events-none leading-none z-10">
                {activeFilterCount}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="md"
            loading={downloading === 'pdf'}
            disabled={sorted.length === 0 || (downloading !== null && downloading !== 'pdf')}
            leftIcon={<FileText size={14} />}
            onClick={handleDownloadPdf}
          >
            {tr('amlBtnPdf')}
          </Button>
          <Button
            variant="secondary"
            size="md"
            loading={downloading === 'csv'}
            disabled={sorted.length === 0 || (downloading !== null && downloading !== 'csv')}
            leftIcon={<FileSpreadsheet size={14} />}
            onClick={handleExportCsv}
          >
            {tr('amlBtnExcel')}
          </Button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-content-muted">
            <Spinner size="md" />
          </div>
        ) : paged.length === 0 ? (
          <TableEmptyRow
            variant={!hasSearched ? 'filter' : 'empty'}
            title={!hasSearched ? tr('amlFilterApplyFiltersToLoadReport') : tr('amlFilterNoTransactionsFound')}
            description={!hasSearched ? tr('amlFilterApplyFiltersToLoadReportDesc') : tr('amlFilterNoTransactionsFoundDesc')}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm" style={{ minWidth: '1200px', width: '100%' }}>
              <colgroup>
                {visibleColList.map((c) => (
                  <col key={c.key} style={{ width: colWidthMap[c.key] }} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b border-subtle bg-surface-secondary/50">
                  {visibleColList.map((c) => (
                    <SortableHeader
                      key={c.key}
                      label={c.label}
                      field={c.key}
                      currentSort={sortField}
                      currentDir={sortDir}
                      onSort={handleSort}
                      align={RIGHT_COLS.has(c.key) ? 'right' : 'left'}
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map((t) => (
                  <tr key={t.id} className="border-b border-subtle hover:bg-surface-secondary transition-colors">
                      {visibleColList.map(({ key }) => {
                        const raw = (t as any)[key];
                        const isRight = RIGHT_COLS.has(key);
                        const isTrunc = TRUNC_COLS.has(key);

                        let display: React.ReactNode;
                        if (MONEY_COLS.has(key)) {
                          display = <span className="whitespace-nowrap">{formatMoney(raw)}</span>;
                        } else if (DATE_COLS.has(key)) {
                          display = <span className="whitespace-nowrap">{raw ? formatDateStr(raw) : '—'}</span>;
                        } else if (isTrunc) {
                          const str = raw ?? '—';
                          display = (
                            <Tooltip content={str} placement="top" onlyWhenTruncated>
                              <span className="block truncate">{str}</span>
                            </Tooltip>
                          );
                        } else {
                          display = <span className="whitespace-nowrap">{raw ?? '—'}</span>;
                        }

                        return (
                          <td
                            key={key}
                            className={`px-4 py-3 ${isRight ? 'text-right' : ''} ${isTrunc ? 'overflow-hidden max-w-0' : ''} text-content`}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                ))}
              </tbody>
              {sorted.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-subtle bg-surface-secondary/50 font-semibold">
                    {visibleColList.map(({ key }, idx) => {
                      const total = totalForCol[key];
                      const isRight = RIGHT_COLS.has(key);
                      return (
                        <td key={key} className={`px-4 py-3 text-sm text-content ${isRight ? 'text-right whitespace-nowrap' : ''}`}>
                          {idx === 0 ? tr('amlTableTotal') : total ?? ''}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {!loading && sorted.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 rounded-xl border border-subtle bg-surface-primary">
          <PageSizeSelect value={pageSize} onChange={handlePageSizeChange} />
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={sorted.length}
            startIndex={(page - 1) * pageSize}
            endIndex={Math.min(page * pageSize, sorted.length)}
          />
        </div>
      )}

      {/* ── Filter Panel ── */}
      <SlidePanel
        isOpen={filterOpen}
        onClose={closeFilterPanel}
        title={tr('amlFilterPanelTitle')}
        width="max-w-[560px]"
      >
        <div className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

            {/* Customer multi-select */}
            <div>
              <Text variant="label" className="block mb-1.5">{tr('amlFilterCustomerLegalName')}</Text>
              {selectedCustomerIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedCustomerIds.map((id) => {
                    const name = customersForFilter.find((c) => c.id === id)?.name ?? id;
                    return (
                      <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-surface-secondary border border-subtle text-xs text-content">
                        {name}
                        <button
                          type="button"
                          onClick={() => setSelectedCustomerIds((prev) => prev.filter((x) => x !== id))}
                          className="text-content-muted hover:text-content transition-colors"
                          aria-label={tr('amlFilterRemoveName', { name })}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <SearchInput
                value={customerSearch}
                onChange={setCustomerSearch}
                  placeholder={tr('amlFilterSearchCustomers')}
                size="sm"
                className="w-full mb-1.5"
              />
              <div className="max-h-40 overflow-y-auto rounded-xl border border-subtle bg-surface-primary divide-y divide-subtle">
                {filteredCustomers.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-content-muted">{tr('amlFilterNoCustomersFound')}</p>
                ) : filteredCustomers.map((c) => (
                  <label key={c.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-surface-secondary transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedCustomerIds.includes(c.id)}
                      onChange={(e) => {
                        setSelectedCustomerIds((prev) =>
                          e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id)
                        );
                      }}
                      className="w-3.5 h-3.5 rounded accent-brand"
                    />
                    <span className="text-sm text-content truncate">{c.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Location multi-select */}
            <div>
              <Text variant="label" className="block mb-1.5">{tr('amlFilterLocation')}</Text>
              {selectedLocationIds.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedLocationIds.map((id) => {
                    const name = locations.find((l) => l.id === id)?.name ?? id;
                    return (
                      <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-surface-secondary border border-subtle text-xs text-content">
                        {name}
                        <button
                          type="button"
                          onClick={() => setSelectedLocationIds((prev) => prev.filter((x) => x !== id))}
                          className="text-content-muted hover:text-content transition-colors"
                          aria-label={tr('amlFilterRemoveName', { name })}
                        >
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <SearchInput
                value={locationSearch}
                onChange={setLocationSearch}
                  placeholder={tr('amlFilterSearchLocations')}
                size="sm"
                className="w-full mb-1.5"
              />
              <div className="max-h-40 overflow-y-auto rounded-xl border border-subtle bg-surface-primary divide-y divide-subtle">
                {filteredLocations.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-content-muted">{tr('amlFilterNoLocationsFound')}</p>
                ) : filteredLocations.map((l) => (
                  <label key={l.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-surface-secondary transition-colors">
                    <input
                      type="checkbox"
                      checked={selectedLocationIds.includes(l.id)}
                      onChange={(e) => {
                        setSelectedLocationIds((prev) =>
                          e.target.checked ? [...prev, l.id] : prev.filter((x) => x !== l.id)
                        );
                      }}
                      className="w-3.5 h-3.5 rounded accent-brand"
                    />
                    <span className="text-sm text-content truncate">{l.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Transaction Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text variant="label" className="block mb-1.5">{tr('amlFilterDateStart')}</Text>
                <DatePicker value={dateFrom} onChange={setDateFrom} placeholder={tr('amlPlaceholderSelectDateStart')} size="sm" />
              </div>
              <div>
                <Text variant="label" className="block mb-1.5">{tr('amlFilterDateEnd')}</Text>
                <DatePicker value={dateTo} onChange={setDateTo} placeholder={tr('amlPlaceholderSelectDateEnd')} size="sm" />
              </div>
            </div>

            {/* Invoice Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text variant="label" className="block mb-1.5">{tr('amlFilterInvoiceDateStart')}</Text>
                <DatePicker value={invDateFrom} onChange={setInvDateFrom} placeholder={tr('amlPlaceholderSelectInvoiceDateStart')} size="sm" />
              </div>
              <div>
                <Text variant="label" className="block mb-1.5">{tr('amlFilterInvoiceDateEnd')}</Text>
                <DatePicker value={invDateTo} onChange={setInvDateTo} placeholder={tr('amlPlaceholderSelectInvoiceDateEnd')} size="sm" />
              </div>
            </div>

            {/* Column visibility */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {ALL_COLUMNS.map((col) => (
                <Checkbox
                  key={col.key}
                  id={`col-${col.key}`}
                  label={col.label}
                  checked={visibleCols[col.key]}
                  onChange={(checked) => setVisibleCols((prev) => ({ ...prev, [col.key]: checked }))}
                />
              ))}
            </div>
          </div>

          {/* footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-subtle shrink-0">
            <Button variant="outline" size="md" onClick={handleResetFilter} disabled={!hasAppliedFilters && !hasPendingFilters}>
              {tr('amlFilterReset')}
            </Button>
            <Button variant="primary" size="md" onClick={handleApplyFilter} disabled={!hasPendingFilters}>
              {tr('amlFilterApply')}
            </Button>
          </div>
        </div>
      </SlidePanel>
    </div>
  );
};

export default TransactionReportView;
